import { useCallback, useState } from 'react';
import { t } from 'ttag';

import {
  CapacitorBarcodeScanner,
  CapacitorBarcodeScannerCameraDirection,
  CapacitorBarcodeScannerTypeHint,
} from '~capacitor-plugins/barcode-scanner';
import { Camera } from '~capacitor-plugins/camera';

enum Permission {
  Prompt = 'prompt',
  PromptWithRationale = 'prompt-with-rationale',
  Granted = 'granted',
}

const checkPermission = async () => {
  const { camera: cameraPermission } = await Camera.checkPermissions();
  if (cameraPermission === Permission.Granted) {
    return true;
  }
  if (
    cameraPermission === Permission.Prompt ||
    cameraPermission === Permission.PromptWithRationale
  ) {
    const { camera: requestedPermission } = await Camera.requestPermissions({
      permissions: ['camera'],
    });
    return requestedPermission === Permission.Granted;
  }
  return false;
};

interface UseScannerProps {
  cameraDirection: CapacitorBarcodeScannerCameraDirection;
  onScanned?: (scanResult?: string) => void;
  onCancel?: () => void;
  onError?: (error: Error) => void;
}

export const useScanner = ({ cameraDirection, onScanned, onCancel, onError }: UseScannerProps) => {
  const [result, setResult] = useState<string>();
  const reset = useCallback(() => {
    setResult(undefined);
  }, []);
  const scan = useCallback(async () => {
    const canScan = await checkPermission();
    if (!canScan) {
      return;
    }
    try {
      const { ScanResult: scanResult } = await CapacitorBarcodeScanner.scanBarcode({
        hint: CapacitorBarcodeScannerTypeHint.QR_CODE,
        cameraDirection,
        scanInstructions: t`Please scan the QR code`,
      });
      setResult(scanResult);
      onScanned?.(scanResult);
    } catch (error) {
      if (error instanceof Error) {
        if (error.message === 'Scanning cancelled') {
          onCancel?.();
        } else {
          // eslint-disable-next-line no-console
          console.error('Caught error', error);
          onError?.(error);
        }
      } else {
        // eslint-disable-next-line no-console
        console.error('Error occurred while scanning:', error);
        onError?.(new Error('An unknown error occurred'));
      }
    }
  }, [cameraDirection, onCancel, onError, onScanned]);

  return {
    result: result ?? '',
    scan,
    reset,
  };
};
